import React, { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { Document, Page, pdfjs } from "react-pdf";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import Tesseract from "tesseract.js";
import "./ResumeOCR.css";
import { useNavigate } from "react-router-dom";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

const PDF_WIDTH = 595;
const PDF_HEIGHT = 842;

// ─────────────────────────────────────────────────────────────────────────────
// Helper: preprocess canvas for better OCR accuracy (grayscale + contrast boost)
// ─────────────────────────────────────────────────────────────────────────────
function preprocessCanvasForOCR(srcCanvas) {
  const out = document.createElement("canvas");
  out.width = srcCanvas.width * 2;
  out.height = srcCanvas.height * 2;

  const ctx = out.getContext("2d");
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(srcCanvas, 0, 0, out.width, out.height);

  const imageData = ctx.getImageData(0, 0, out.width, out.height);
  const data = imageData.data;

  for (let i = 0; i < data.length; i += 4) {
    const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    const contrasted = Math.min(255, Math.max(0, 1.8 * (gray - 128) + 128));
    const final =
      contrasted < 140 ? contrasted * 0.6 : Math.min(255, contrasted * 1.1);
    data[i] = data[i + 1] = data[i + 2] = final;
  }

  ctx.putImageData(imageData, 0, 0);
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper: crop a region from the react-pdf rendered <canvas> element
// ─────────────────────────────────────────────────────────────────────────────
function cropPdfCanvas(pdfCanvasEl, crop, displayWidth, displayHeight) {
  const scaleX = pdfCanvasEl.width / displayWidth;
  const scaleY = pdfCanvasEl.height / displayHeight;

  const out = document.createElement("canvas");
  out.width = Math.round(crop.width * scaleX);
  out.height = Math.round(crop.height * scaleY);

  out
    .getContext("2d")
    .drawImage(
      pdfCanvasEl,
      Math.round(crop.x * scaleX),
      Math.round(crop.y * scaleY),
      out.width,
      out.height,
      0,
      0,
      out.width,
      out.height,
    );
  return out;
}

function ProcessedResumeOCR() {
  const selectedFieldRef = useRef(null);
  const navigate = useNavigate();

  // ── PDF Viewer state ──────────────────────────────────────────────────────
  const pdfContainerRef = useRef(null);
  const pdfWrapperRef = useRef(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfScale, setPdfScale] = useState(1);

  // ── OCR / crop state ──────────────────────────────────────────────────────
  const [crop, setCrop] = useState(null);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [ocrProgress, setOcrProgress] = useState(0);
  const [ocrMode, setOcrMode] = useState("idle");
  const [pdfPageSize, setPdfPageSize] = useState({ width: 0, height: 0 });

  // ── Photo & Signature state ───────────────────────────────────────────────
  const [photoSignUploading, setPhotoSignUploading] = useState(false);
  const [cropPreview, setCropPreview] = useState(null);

  // ── Validation states ─────────────────────────────────────────────────────
  const [validationErrors, setValidationErrors] = useState({
    mobile: "",
    email: "",
    aadhar: "",
  });

  // ── Post options for dropdown ─────────────────────────────────────────────
  const postOptions = [
    { hindi: "स्टेनो टाइपिस्ट", english: "Steno Typist" },
    { hindi: "आशुलिपिक", english: "Stenographer" },
    { hindi: "सूचीकार (केटलॉग)", english: "Cataloguer" },
    { hindi: "ओवरसियर", english: "Overseer" },
    { hindi: "प्रयोगशाला सहायक", english: "Laboratory Assistant" },
    { hindi: "लिपिक / कनिष्ठ सहायक", english: "Clerk / Junior Assistant" },
  ];

  const vibhagOptions = [
    { hindi: "मनोविज्ञान विभाग", english: "Psychology Department" },
    { hindi: "सैन्य अध्ययन विभाग", english: "Military Studies Department" },
    { hindi: "गणित विभाग", english: "Mathematics Department" },
    { hindi: "रसायन विज्ञान विभाग", english: "Chemistry Department" },
    { hindi: "भौतिक विज्ञान विभाग", english: "Physics Department" },
    { hindi: "जन्तु विज्ञान विभाग", english: "Zoology Department" },
    { hindi: "वनस्पति विज्ञान विभाग", english: "Botany Department" },
  ];

  const paymentOptions = ["Bank Draft", "NEFT", "Check", "UPI", "IMPS", "RTGS"];

  // ── Refs ──────────────────────────────────────────────────────────────────
  const selectedFieldStateRef = useRef(null);
  const candidatesRef = useRef([]);
  const currentIndexRef = useRef(0);
  const currentFolderRef = useRef("");
  const ocrLoadingRef = useRef(false);
  const ocrStatusRef = useRef("");
  const formRef = useRef({
    post: "",
    vibhag: "",
    nameEnglish: "",
    nameHindi: "",
    fatherName: "",
    dob: "",
    gender: "",
    category: "",
    address: "",
    mobile: "",
    email: "",
    pinCode: "",
    religion: "",
    caste: "",
    hindiTyping: "",
    englishTyping: "",
    shorthandSpeed: "",
    aadharNumber: "",
    paymentMethod: "",
    paymentNumber: "",
  });
  const eduRowsRef = useRef([]);
  const workRowsRef = useRef([]);

  // ── UPDATED defaultEduRows (dynamic, matches ResumeOCR) ──────────────────
  const defaultEduRows = [
    {
      id: 1,
      exam: "",
      customExam: "",
      board: "",
      isBoardChecked: false,
      totalMarks: "",
      obtainedMarks: "",
      percentage: "",
      scoreType: "Percentage",
    },
  ];

  const defaultWorkRows = [
    { id: 1, orgName: "", duration: "", post: "", salary: "" },
  ];

  // ── State ─────────────────────────────────────────────────────────────────
  const [selectedField, setSelectedField] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrStatus, setOcrStatus] = useState("");
  const [form, setForm] = useState(formRef.current);
  console.log("form ka data ->", form);
  const [eduRows, setEduRows] = useState(defaultEduRows);
  const [workRows, setWorkRows] = useState(defaultWorkRows);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [currentPdfUrl, setCurrentPdfUrl] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);

  // ── Helper function to extract numeric ID from folder name ─────────────────
  const extractNumericId = (folderName) => {
    if (!folderName) return Infinity;
    // Extract numbers from folder name (e.g., "BUNDLE_123" -> 123)
    const match = folderName.match(/\d+/);
    return match ? parseInt(match[0], 10) : Infinity;
  };

  // ── Sort candidates by folder ID in increasing order ──────────────────────
  const sortCandidatesByFolderId = (candidatesList) => {
    return [...candidatesList].sort((a, b) => {
      const idA = extractNumericId(a.folder);
      const idB = extractNumericId(b.folder);
      return idA - idB;
    });
  };

  // ── Validation functions ──────────────────────────────────────────────────
  const validateMobile = (mobile) => {
    if (!mobile) return "";
    return /^\d{10}$/.test(mobile)
      ? ""
      : "कृपया 10 अंकों का मोबाइल नंबर दर्ज करें";
  };
  const validateEmail = (email) => {
    if (!email) return "";
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)
      ? ""
      : "कृपया वैध ईमेल आईडी दर्ज करें (जैसे: name@example.com)";
  };
  const validateAadhar = (aadhar) => {
    if (!aadhar) return "";
    return /^\d{12}$/.test(aadhar)
      ? ""
      : "कृपया 12 अंकों का आधार नंबर दर्ज करें";
  };
  const validatePinCode = (pin) => {
    if (!pin) return "";
    return /^\d{6}$/.test(pin) ? "" : "6 digit pin code daalo";
  };

  const handleMobileChange = (value) => {
    const digitsOnly = value.replace(/\D/g, "");
    setValidationErrors((p) => ({ ...p, mobile: validateMobile(digitsOnly) }));
    const u = { ...formRef.current, mobile: digitsOnly };
    setForm(u);
    formRef.current = u;
  };
  const handleEmailChange = (value) => {
    setValidationErrors((p) => ({ ...p, email: validateEmail(value) }));
    const u = { ...formRef.current, email: value };
    setForm(u);
    formRef.current = u;
  };
  const handleAadharChange = (value) => {
    const digitsOnly = value.replace(/\D/g, "");
    setValidationErrors((p) => ({ ...p, aadhar: validateAadhar(digitsOnly) }));
    const u = { ...formRef.current, aadharNumber: digitsOnly };
    setForm(u);
    formRef.current = u;
  };
  const handlePinCodeChange = (value) => {
    const digitsOnly = value.replace(/\D/g, "").slice(0, 6);

    setValidationErrors((p) => ({
      ...p,
      pinCode: validatePinCode(digitsOnly),
    }));

    const u = { ...formRef.current, pinCode: digitsOnly };
    setForm(u);
    formRef.current = u;
  };

  // ── PDF Viewer helpers ────────────────────────────────────────────────────
  const calcFit = () => {
    if (!pdfContainerRef.current) return;
    const w = pdfContainerRef.current.offsetWidth - 48;
    const h = pdfContainerRef.current.offsetHeight - 32;
    setPdfScale(Math.min(w / PDF_WIDTH, h / PDF_HEIGHT));
  };
  const fitWidth = () => {
    if (pdfContainerRef.current)
      setPdfScale((pdfContainerRef.current.offsetWidth - 48) / PDF_WIDTH);
  };
  const fitHeight = () => {
    if (pdfContainerRef.current)
      setPdfScale((pdfContainerRef.current.offsetHeight - 32) / PDF_HEIGHT);
  };
  const zoomIn = () => setPdfScale((s) => Math.min(+(s + 0.15).toFixed(2), 4));
  const zoomOut = () =>
    setPdfScale((s) => Math.max(+(s - 0.15).toFixed(2), 0.2));

  function onPdfLoadSuccess({ numPages }) {
    setNumPages(numPages);
    setPageNumber(1);
  }

  function onPdfPageRenderSuccess() {
    if (!pdfWrapperRef.current) return;
    const canvas = pdfWrapperRef.current.querySelector("canvas");
    if (canvas)
      setPdfPageSize({
        width: canvas.offsetWidth,
        height: canvas.offsetHeight,
      });
  }

  useEffect(()=>{
        const login = sessionStorage.getItem("workerToken");
        if(!login || undefined){
          alert("Login First");
          navigate("/");
        }
      });

  useEffect(() => {
    calcFit();
    window.addEventListener("resize", calcFit);
    return () => window.removeEventListener("resize", calcFit);
  }, []);

  // ── Sync refs ─────────────────────────────────────────────────────────────
  useEffect(() => {
    selectedFieldRef.current = selectedField;
    selectedFieldStateRef.current = selectedField;
    candidatesRef.current = candidates;
    currentIndexRef.current = currentIndex;
    ocrLoadingRef.current = ocrLoading;
    ocrStatusRef.current = ocrStatus;
    formRef.current = form;
    eduRowsRef.current = eduRows;
    workRowsRef.current = workRows;
  }, [
    selectedField,
    candidates,
    currentIndex,
    ocrLoading,
    ocrStatus,
    form,
    eduRows,
    workRows,
  ]);

  // ── Fetch on mount ────────────────────────────────────────────────────────
  useEffect(() => {
    fetchCandidates();
  }, []);

  const fetchCandidates = async (preserveOrder = false) => {
    setPageLoading(!preserveOrder);
    try {
      const phoneNumber = sessionStorage.getItem("phoneNumber");
      if (!phoneNumber) {
        alert("Phone number not found. Please login again.");
        navigate("/login");
        return;
      }

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/processed-candidates`,
        { params: { phonenumber: phoneNumber },
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("workerToken")}`
            }} ,
         
      );

      // console.log("candidateData ->", res.data);

      if (res.data.success && res.data.data?.length > 0) {
        let newData = res.data.data;
        
        // Sort by folder ID in increasing order
        newData = sortCandidatesByFolderId(newData);

        if (preserveOrder && candidatesRef.current.length > 0) {
          // ── STABLE MERGE: keep old order, only refresh candidate data ──
          const newMap = new Map(newData.map(item => [item.folder, item]));

          // Replace updated data in-place, preserving index positions
          const stableData = candidatesRef.current.map(
            oldItem => newMap.get(oldItem.folder) || oldItem
          );

          // Append any brand-new candidates at the end
          const existingFolders = new Set(candidatesRef.current.map(i => i.folder));
          const newItems = newData.filter(i => !existingFolders.has(i.folder));
          let finalData = [...stableData, ...newItems];
          
          // Sort the final data again to maintain order
          finalData = sortCandidatesByFolderId(finalData);
          
          setCandidates(finalData);
          candidatesRef.current = finalData;

          // Refresh current candidate's data without changing index
          const refreshedCurrent = finalData[currentIndexRef.current];
          if (refreshedCurrent) {
            loadCandidate(refreshedCurrent);
          }
        } else {
          // ── INITIAL LOAD: use sorted data ──
          setCandidates(newData);
          candidatesRef.current = newData;
          loadCandidate(newData[0]);
          setCurrentIndex(0);
          currentIndexRef.current = 0;
        }
      } else {
        setCandidates([]);
        candidatesRef.current = [];
        clearForm();
      }
    } catch (err) {
      console.error(err);
      alert("Failed to fetch candidates");
    } finally {
      setPageLoading(false);
    }
  };

  const loadCandidate = (candidateData) => {
    if (!candidateData) return;

    setPdfLoading(true);
    currentFolderRef.current = candidateData?.folder;

    // ───────── RESET STATES ─────────
    setCrop(null);
    setCompletedCrop(null);
    setOcrMode("idle");
    setCropPreview(null);
    setPhotoSignUploading(false);

    // ───────── PDF ─────────
    if (candidateData?.pdf_url) {
      setCurrentPdfUrl(candidateData.pdf_url);
      setNumPages(null);
      setPageNumber(1);
    } else {
      setCurrentPdfUrl(null);
    }

    // ───────── FORM (DB → FRONTEND MAPPING) ─────────
    const f = {
      post: candidateData?.post || "",
      vibhag: candidateData?.vibhag || "",

      nameEnglish: candidateData?.nameEnglish || "",
      nameHindi: candidateData?.nameHindi || "",
      fatherName: candidateData?.fatherName || "",

      pinCode: candidateData?.pinCode || candidateData?.pin_code || "",

      dob: candidateData?.dob || "",
      gender: candidateData?.gender || "",
      category: candidateData?.category || "",

      address: candidateData?.address || "",
      correspondenceAddress: candidateData?.correspondenceAddress || "",

      mobile: candidateData?.mobile || "",
      email: candidateData?.email || "",

      religion: candidateData?.religion || "",
      caste: candidateData?.caste || "",

      hindiTyping: candidateData?.hindiTyping || "",
      englishTyping: candidateData?.englishTyping || "",
      shorthandSpeed: candidateData?.shorthandSpeed || "",

      aadharNumber: candidateData?.aadharNumber || "",

      paymentMethod: candidateData?.paymentMethod || "",
      paymentNumber: candidateData?.paymentNumber || "",
    };

    console.log("f data ->", candidateData);
    setForm(f);
    formRef.current = f;

    setValidationErrors({
      mobile: validateMobile(f.mobile || ""),
      email: validateEmail(f.email || ""),
      aadhar: validateAadhar(f.aadharNumber || ""),
    });

    // ───────── EDUCATION ─────────
    let edu = [];

    try {
      let eduSource = candidateData?.edu_rows;

      while (typeof eduSource === "string") {
        eduSource = JSON.parse(eduSource);
      }

      if (!Array.isArray(eduSource)) {
        eduSource = eduSource ? [eduSource] : [];
      }

      edu = eduSource.map((row, i) => ({
        id: Date.now() + i,
        exam: row.l || row.exam || row.label || "",
        customExam: "",
        board: row.board || "",
        totalMarks: row.total || row.totalMarks || "",
        obtainedMarks: row.obtain || row.obtainedMarks || "",
        percentage: row.percentage || "",
        scoreType: row.scoreType || "Percentage",
      }));
    } catch (err) {
      console.error("❌ EDU ERROR:", err);
    }

    if (edu.length === 0) {
      edu = [
        {
          id: Date.now(),
          exam: "",
          customExam: "",
          board: "",
          totalMarks: "",
          obtainedMarks: "",
          percentage: "",
          scoreType: "Percentage",
        },
      ];
    }

    setEduRows(edu);
    eduRowsRef.current = edu;

    // ───────── WORK ─────────
    let work = [];

    try {
      let workSource = candidateData?.work_rows;

      while (typeof workSource === "string") {
        workSource = JSON.parse(workSource);
      }

      if (Array.isArray(workSource)) {
        work = workSource.map((row, i) => ({
          id: row.id || i + 1,
          orgName: row.o || row.orgName || "",
          post: row.p || row.post || "",
          duration: row.d || row.duration || "",
          salary: row.s || row.salary || "",
        }));
      }
    } catch (err) {
      console.error("❌ WORK ERROR:", err);
    }

    if (work.length === 0) {
      work = [{ id: 1, orgName: "", duration: "", post: "", salary: "" }];
    }

    setWorkRows(work);
    workRowsRef.current = work;

    setPdfLoading(false);
  };

  const clearForm = () => {
    const e = {
      post: "",
      vibhag: "",
      nameEnglish: "",
      nameHindi: "",
      fatherName: "",
      dob: "",
      gender: "",
      category: "",
      address: "",
      mobile: "",
      email: "",
      religion: "",
      caste: "",
      hindiTyping: "",
      englishTyping: "",
      shorthandSpeed: "",
      aadharNumber: "",
      paymentMethod: "",
      paymentNumber: "",
    };
    setForm(e);
    formRef.current = e;
    setEduRows(defaultEduRows);
    eduRowsRef.current = defaultEduRows;
    setWorkRows(defaultWorkRows);
    workRowsRef.current = defaultWorkRows;
    setCurrentPdfUrl(null);
    currentFolderRef.current = "";
    setValidationErrors({ mobile: "", email: "", aadhar: "" });
    setCrop(null);
    setCompletedCrop(null);
    setOcrMode("idle");
    setCropPreview(null);
    setPhotoSignUploading(false);
  };

  const updateCandidate = async () => {
    const mobileError = validateMobile(formRef.current.mobile);
    const emailError = validateEmail(formRef.current.email);
    const aadharError = validateAadhar(formRef.current.aadharNumber);

    setValidationErrors({
      mobile: mobileError,
      email: emailError,
      aadhar: aadharError,
    });

    if (mobileError || emailError || aadharError) {
      alert("❌ Valid mobile, email aur aadhar number daalo");
      return;
    }

    const folder = currentFolderRef.current;
    if (!folder) {
      alert("No candidate selected");
      return;
    }

    const payload = {
      form: formRef.current,
      eduRows: eduRowsRef.current,
      workRows: workRowsRef.current,
    };

    console.log("FINAL PAYLOAD:", payload);

    try {
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}/update-candidate/${folder}`,
        payload,
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("workerToken")}`
            }
          }
      );

      if (res.data.success) {
        alert("✅ Candidate updated successfully!");
        await fetchCandidates(true);
      } else {
        alert("❌ Update failed: " + res.data.message);
      }
    } catch (e) {
      console.error(e);
      alert("❌ Error: " + (e.response?.data?.message || e.message));
    }
  };

  const deleteCandidate = async () => {
    const folder = currentFolderRef.current;
    if (!folder) return;
    if (!window.confirm("Are you sure you want to delete this candidate?")) return;

    try {
      const res = await axios.delete(
        `${process.env.REACT_APP_API_URL}/delete-candidate/${folder}`,{
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("workerToken")}`
            }}
      );

      if (res.data.success) {
        alert("✅ Candidate deleted successfully!");

        const updated = candidatesRef.current.filter(c => c.folder !== folder);
        const sortedUpdated = sortCandidatesByFolderId(updated);
        
        setCandidates(sortedUpdated);
        candidatesRef.current = sortedUpdated;

        if (sortedUpdated.length > 0) {
          const newIndex = Math.min(currentIndexRef.current, sortedUpdated.length - 1);
          setCurrentIndex(newIndex);
          currentIndexRef.current = newIndex;
          loadCandidate(sortedUpdated[newIndex]);
        } else {
          clearForm();
        }
      }
    } catch (e) {
      console.error(e);
      alert("❌ Delete करने में त्रुटि हुई: " + (e.response?.data?.message || e.message));
    }
  };

  const nextCandidate = () => {
    if (currentIndexRef.current < candidatesRef.current.length - 1) {
      const i = currentIndexRef.current + 1;
      setCurrentIndex(i);
      currentIndexRef.current = i;
      loadCandidate(candidatesRef.current[i]);
      setValidationErrors({ mobile: "", email: "", aadhar: "" });
    }
  };

  const prevCandidate = () => {
    if (currentIndexRef.current > 0) {
      const i = currentIndexRef.current - 1;
      setCurrentIndex(i);
      currentIndexRef.current = i;
      loadCandidate(candidatesRef.current[i]);
      setValidationErrors({ mobile: "", email: "", aadhar: "" });
    }
  };

  // ── OCR ───────────────────────────────────────────────────────────────────
  const runOCR = useCallback(async () => {
    if (!selectedFieldRef.current) {
      alert("पहले कोई field पर click करें");
      return;
    }
    if (!completedCrop || !completedCrop.width || !completedCrop.height) {
      alert("PDF पर कोई क्षेत्र चुनें (खींचकर select करें)");
      return;
    }

    const pdfCanvas = pdfWrapperRef.current?.querySelector("canvas");
    if (!pdfCanvas) {
      alert("PDF canvas नहीं मिला। PDF लोड होने तक प्रतीक्षा करें।");
      return;
    }

    const cropped = cropPdfCanvas(
      pdfCanvas,
      completedCrop,
      pdfPageSize.width,
      pdfPageSize.height,
    );
    const processedCanvas = preprocessCanvasForOCR(cropped);

    setOcrLoading(true);
    ocrLoadingRef.current = true;
    setOcrStatus("loading");
    ocrStatusRef.current = "loading";
    setOcrMode("processing");
    setOcrProgress(0);

    try {
      const result = await Tesseract.recognize(processedCanvas, "eng+hin", {
        logger: (m) => {
          if (m.status === "recognizing text")
            setOcrProgress(Math.round(m.progress * 100));
        },
        tessedit_pageseg_mode: "6",
      });

      const text = result.data.text.trim();
      const field = selectedFieldRef.current;
      const updated = { ...formRef.current, [field]: text };
      setForm(updated);
      formRef.current = updated;

      setOcrStatus("done");
      ocrStatusRef.current = "done";
      setOcrMode("done");
      setCrop(null);
      setCompletedCrop(null);
    } catch (err) {
      console.error("Tesseract error:", err);
      setOcrStatus("error");
      ocrStatusRef.current = "error";
      setOcrMode("error");
    } finally {
      setOcrLoading(false);
      ocrLoadingRef.current = false;
    }
  }, [completedCrop, pdfPageSize]);

  // ── Crop-based photo / signature upload ───────────────────────────────────
  const uploadCroppedImage = async (type) => {
    if (!completedCrop || !completedCrop.width || !completedCrop.height) {
      alert("पहले PDF पर कोई क्षेत्र खींचें (drag करके select करें)");
      return;
    }
    const folder = currentFolderRef.current;
    if (!folder) {
      alert("Folder not found");
      return;
    }

    const pdfCanvas = pdfWrapperRef.current?.querySelector("canvas");
    if (!pdfCanvas) {
      alert("PDF canvas नहीं मिला। PDF लोड होने तक प्रतीक्षा करें।");
      return;
    }

    const cropped = cropPdfCanvas(
      pdfCanvas,
      completedCrop,
      pdfPageSize.width,
      pdfPageSize.height,
    );
    const base64 = cropped.toDataURL("image/jpeg", 0.95);
    setCropPreview({ base64, type });
    setPhotoSignUploading(type);

    try {
      const payload = {
        folderName: folder,
        photo: type === "photo" ? base64 : null,
        signature: type === "signature" ? base64 : null,
      };
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/upload-photo-signature`,
        payload,
        {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("workerToken")}`
            }}
      );
      if (res.data.success) {
        alert(
          `✅ ${type === "photo" ? "फोटो" : "हस्ताक्षर"} सफलतापूर्वक सहेजा गया!`,
        );
        setCrop(null);
        setCompletedCrop(null);
        setOcrMode("idle");
        setCropPreview(null);
      } else {
        alert("❌ Upload failed");
        setCropPreview(null);
      }
    } catch (err) {
      console.error(err);
      alert(
        "❌ Upload failed: " + (err.response?.data?.message || err.message),
      );
      setCropPreview(null);
    } finally {
      setPhotoSignUploading(false);
    }
  };

  const handleCropChange = (c) => {
    setCrop(c);
    if (ocrMode === "idle" || ocrMode === "done" || ocrMode === "error")
      setOcrMode("selecting");
  };

  // ── Education / Work helpers ──────────────────────────────────────────────
  const calculatePercentage = (obtained, total) => {
    const o = parseFloat(obtained),
      t = parseFloat(total);
    if (!Number.isFinite(o) || !Number.isFinite(t) || t <= 0) return "";
    return ((o / t) * 100).toFixed(2);
  };

  const updateEduRow = (id, field, value) => {
    const rows = eduRowsRef.current.map((row) => {
      if (row.id !== id) return row;
      let updatedRow = { ...row, [field]: value };

      if (field === "scoreType") {
        updatedRow.percentage = "";
      }

      if (field === "totalMarks" || field === "obtainedMarks") {
        const scoreType = updatedRow.scoreType || "Percentage";
        if (scoreType === "Percentage") {
          const total = field === "totalMarks" ? value : updatedRow.totalMarks;
          const obtained =
            field === "obtainedMarks" ? value : updatedRow.obtainedMarks;
          updatedRow.percentage = calculatePercentage(obtained, total);
        } else if (scoreType === "CGPA" || scoreType === "Grade") {
          const obtainedValue =
            field === "obtainedMarks" ? value : updatedRow.obtainedMarks;
          updatedRow.percentage = obtainedValue || "";
        }
      }

      return updatedRow;
    });
    setEduRows(rows);
    eduRowsRef.current = rows;
  };

  const addEduRow = () => {
    const newRow = {
      id: Date.now(),
      exam: "",
      customExam: "",
      board: "",
      isBoardChecked: false,
      totalMarks: "",
      obtainedMarks: "",
      percentage: "",
      scoreType: "Percentage",
    };
    const rows = [...eduRowsRef.current, newRow];
    setEduRows(rows);
    eduRowsRef.current = rows;
  };

  const deleteEduRow = (id) => {
    const rows = eduRowsRef.current.filter((r) => r.id !== id);
    setEduRows(rows);
    eduRowsRef.current = rows;
  };

  const updateWorkRow = (id, field, value) => {
    const rows = workRowsRef.current.map((r) =>
      r.id === id ? { ...r, [field]: value } : r,
    );
    setWorkRows(rows);
    workRowsRef.current = rows;
  };

  const addWorkRow = () => {
    const rows = [
      ...workRowsRef.current,
      {
        id: workRowsRef.current.length + 1,
        orgName: "",
        duration: "",
        post: "",
        salary: "",
      },
    ];
    setWorkRows(rows);
    workRowsRef.current = rows;
  };

  const fieldClass = (name) => {
    let c = "field-input";
    if (selectedFieldRef.current === name) c += " active";
    if (ocrLoadingRef.current && selectedFieldRef.current === name)
      c += " loading-field";
    return c;
  };

  // ── Loading Screen ────────────────────────────────────────────────────────
  if (pageLoading) {
    return (
      <div
        className="container"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "80vh",
        }}
      >
        <div className="pdf-loading">
          <div className="spinner"></div>
          <p>Processed candidates load हो रहे हैं...</p>
        </div>
      </div>
    );
  }

  // ── No Candidates ─────────────────────────────────────────────────────────
  if (candidates.length === 0) {
    return (
      <div className="container">
        <div className="header">
          <div className="header-icon">📋</div>
          <div>
            <h1 className="title">Processed Candidates</h1>
            <p className="subtitle">Edit &amp; Update processed data</p>
          </div>
          <button className="nav-btn" onClick={() => navigate("/ResumeOCR")}>
            ← OCR पर जाएं
          </button>
        </div>
        <div
          style={{
            textAlign: "center",
            padding: "60px 24px",
            color: "var(--text-2)",
          }}
        >
          <div style={{ fontSize: 48, marginBottom: 16 }}>📭</div>
          <h2 style={{ color: "var(--text)", marginBottom: 8 }}>
            कोई processed candidate नहीं मिला
          </h2>
          <p style={{ marginBottom: 24 }}>
            पहले OCR page पर जाकर candidates process करें।
          </p>
          <button className="nav-btn" onClick={() => navigate("/ResumeOCR")}>
            OCR पर जाएं
          </button>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="container">
      {/* ── Header ── */}
      <div className="header">
        <div className="header-icon">📋</div>
        <div>
          <h1 className="title">Processed Candidates</h1>
          <p className="subtitle">Edit &amp; Update processed candidate data</p>
        </div>
        <button className="nav-btn" onClick={() => navigate("/ResumeOCR")}>
          ← OCR पर जाएं
        </button>
      </div>

      {/* ── Candidate Navigation ── */}
      <div className="candidate-bar">
        <button
          className="nav-btn"
          onClick={prevCandidate}
          disabled={currentIndex === 0}
        >
          ◀ पिछला अभ्यर्थी
        </button>

        <div className="candidate-badge">
          <span className="candidate-label">अभ्यर्थी</span>

          {/* ── Dropdown Selector ── */}
          <select
            value={currentIndex}
            onChange={(e) => {
              const i = parseInt(e.target.value, 10);
              if (i >= 0 && i < candidates.length) {
                setCurrentIndex(i);
                currentIndexRef.current = i;
                loadCandidate(candidates[i]);
                setValidationErrors({ mobile: "", email: "", aadhar: "" });
              }
            }}
            style={{
              color: "var(--text, #f1f5f9)",
              border: "1px solid rgba(255,255,255,0.15)",
              borderRadius: 6,
              padding: "4px 10px",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              maxWidth: 220,
              outline: "none",
            }}
          >
            {candidates.length === 0 ? (
              <option value="">कोई अभ्यर्थी नहीं</option>
            ) : (
              candidates.map((c, i) => (
                <option key={i} value={i}>
                  {i + 1}. {c.folder || `Candidate-${i + 1}`}
                </option>
              ))
            )}
          </select>

          <span className="candidate-count">
            {currentIndex + 1} / {candidates.length}
          </span>
        </div>

        <button
          className="nav-btn"
          onClick={nextCandidate}
          disabled={currentIndex === candidates.length - 1}
        >
          अगला अभ्यर्थी ▶
        </button>
      </div>

      <div className="main">
        {/* ══════════════════════════════════════════════════════════════════
            PDF PANEL (LEFT)
        ══════════════════════════════════════════════════════════════════ */}
        <div className="image-panel">
          <div className="panel-header">
            <span className="panel-icon">📄</span>
            <h2>PDF दस्तावेज़</h2>
          </div>

          {/* Sticky Toolbar */}
          <div
            className="toolbar"
            style={{
              position: "sticky",
              top: 0,
              zIndex: 100,
              borderBottom: "1px solid rgba(255,255,255,0.08)",
              flexWrap: "wrap",
              gap: 6,
            }}
          >
            {/* Page navigation */}
            <div className="toolbar-group">
              <button
                className="toolbar-btn"
                disabled={pageNumber <= 1}
                onClick={() => setPageNumber((p) => p - 1)}
                title="Previous Page"
              >
                <span className="btn-icon">&#9664;</span>
                <span className="btn-label">Prev</span>
              </button>
              <span className="page-info">
                <span className="page-current">{pageNumber}</span>
                <span className="page-sep">/</span>
                <span className="page-total">{numPages ?? "—"}</span>
              </span>
              <button
                className="toolbar-btn"
                disabled={!numPages || pageNumber >= numPages}
                onClick={() => setPageNumber((p) => p + 1)}
                title="Next Page"
              >
                <span className="btn-label">Next</span>
                <span className="btn-icon">&#9654;</span>
              </button>
            </div>

            <div className="toolbar-divider" />

            {/* Zoom */}
            <div className="toolbar-group">
              <button
                className="toolbar-btn icon-only"
                onClick={zoomOut}
                title="Zoom Out"
              >
                &#8722;
              </button>
              <span className="zoom-label">{Math.round(pdfScale * 100)}%</span>
              <button
                className="toolbar-btn icon-only"
                onClick={zoomIn}
                title="Zoom In"
              >
                &#43;
              </button>
            </div>

            <div className="toolbar-divider" />

            {/* Fit */}
            <div className="toolbar-group">
              <button
                className="toolbar-btn"
                onClick={fitWidth}
                title="Fit Width"
              >
                &#8596; Width
              </button>
              <button
                className="toolbar-btn"
                onClick={fitHeight}
                title="Fit Height"
              >
                &#8597; Height
              </button>
              <button
                className="toolbar-btn"
                onClick={calcFit}
                title="Fit Page"
              >
                &#9974; Fit
              </button>
            </div>

            <div className="toolbar-divider" />

            {/* Scan button */}
            <div className="toolbar-group" style={{ marginLeft: "auto" }}>
              <button
                onClick={runOCR}
                disabled={ocrMode === "processing"}
                title="PDF से Scan करें (पहले field चुनें, फिर PDF पर खींचें)"
                style={{
                  padding: "6px 14px",
                  borderRadius: 6,
                  border: "none",
                  cursor: ocrMode === "processing" ? "not-allowed" : "pointer",
                  fontWeight: 700,
                  fontSize: 12,
                  color: "#fff",
                  background:
                    ocrMode === "processing"
                      ? "#374151"
                      : "linear-gradient(135deg, #1d4ed8, #1e40af)",
                  boxShadow:
                    ocrMode === "processing"
                      ? "none"
                      : "0 2px 6px rgba(29,78,216,0.45)",
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  whiteSpace: "nowrap",
                  transition: "all 0.2s ease",
                  opacity: ocrMode === "processing" ? 0.7 : 1,
                }}
              >
                {ocrMode === "processing" ? (
                  <>
                    <span
                      style={{
                        display: "inline-block",
                        width: 11,
                        height: 11,
                        border: "2px solid rgba(255,255,255,0.4)",
                        borderTopColor: "#fff",
                        borderRadius: "50%",
                        animation: "ocr-spin 0.7s linear infinite",
                        flexShrink: 0,
                      }}
                    />
                    {ocrProgress}%
                  </>
                ) : (
                  <>🔍 Scan करें</>
                )}
              </button>
            </div>
          </div>

          {/* OCR status bar */}
          <div
            className={`ocr-status-bar ${ocrStatus}`}
            style={{ margin: "0", borderRadius: 0 }}
          >
            {ocrMode === "idle" && !selectedFieldRef.current && (
              <span className="status-hint">
                👆 पहले कोई field पर click करें
              </span>
            )}
            {ocrMode === "idle" && selectedFieldRef.current && (
              <span className="status-hint active">
                ✏️ फ़ील्ड चुना: <strong>{selectedFieldRef.current}</strong> — अब
                PDF पर क्षेत्र खींचें
              </span>
            )}
            {ocrMode === "selecting" && (
              <span className="status-hint active">
                ✂️ क्षेत्र चुना जा रहा है — &quot;Scan करें&quot; दबाएं
              </span>
            )}
            {ocrMode === "processing" && (
              <span className="status-loading">
                ⏳ OCR चल रहा है ({ocrProgress}%) —{" "}
                <strong>{selectedFieldRef.current}</strong>
              </span>
            )}
            {ocrMode === "done" && (
              <span className="status-done">
                ✅ Text भर दिया गया: <strong>{selectedFieldRef.current}</strong>
              </span>
            )}
            {ocrMode === "error" && (
              <span className="status-error">
                ❌ OCR में त्रुटि हुई, दोबारा कोशिश करें
              </span>
            )}
          </div>

          {/* PDF Area with ReactCrop overlay */}
          <div className="pdf-area" ref={pdfContainerRef}>
            {currentPdfUrl ? (
              <div
                style={{ position: "relative", display: "inline-block" }}
                ref={pdfWrapperRef}
              >
                {pdfLoading && (
                  <div className="pdf-loading">
                    <div className="spinner"></div>
                    <p>PDF लोड हो रहा है...</p>
                  </div>
                )}
                <Document
                  file={currentPdfUrl}
                  onLoadSuccess={onPdfLoadSuccess}
                  loading={<div className="pdf-loading">Loading PDF…</div>}
                  error={<div className="pdf-error">Failed to load PDF.</div>}
                >
                  <Page
                    pageNumber={pageNumber}
                    scale={pdfScale}
                    renderAnnotationLayer={false}
                    renderTextLayer={true}
                    onRenderSuccess={onPdfPageRenderSuccess}
                  />
                </Document>

                {pdfPageSize.width > 0 && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: pdfPageSize.width,
                      height: pdfPageSize.height,
                      zIndex: 10,
                    }}
                  >
                    <ReactCrop
                      crop={crop}
                      onChange={handleCropChange}
                      onComplete={(c) => setCompletedCrop(c)}
                      style={{
                        display: "block",
                        width: "100%",
                        height: "100%",
                      }}
                    >
                      <img
                        src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
                        alt=""
                        style={{
                          width: pdfPageSize.width,
                          height: pdfPageSize.height,
                          opacity: 0,
                          display: "block",
                          userSelect: "none",
                          pointerEvents: "none",
                        }}
                        draggable={false}
                      />
                    </ReactCrop>
                  </div>
                )}

                {ocrMode === "processing" && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: 4,
                      background: "#222",
                      zIndex: 20,
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: `${ocrProgress}%`,
                        background: "#57c8ff",
                        transition: "width .3s ease",
                      }}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="pdf-loading">
                <span style={{ fontSize: 40 }}>📄</span>
                <p>PDF उपलब्ध नहीं है</p>
              </div>
            )}
          </div>

          {/* OCR language note */}
          <div className="ocr-section" style={{ padding: "8px 12px" }}>
            <div
              style={{
                fontSize: 11,
                color: "#9ca3af",
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
              }}
            >
              <span
                style={{
                  display: "inline-block",
                  padding: "2px 8px",
                  background: "rgba(29,78,216,0.15)",
                  border: "1px solid rgba(29,78,216,0.3)",
                  borderRadius: 4,
                  color: "#93c5fd",
                  fontWeight: 600,
                  fontSize: 10,
                  letterSpacing: "0.05em",
                }}
              >
                eng+hin
              </span>
              Hindi &amp; English OCR सक्रिय है
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════════════
            FORM PANEL (RIGHT)
        ══════════════════════════════════════════════════════════════════ */}
        <div className="form-panel">
          <div className="panel-header">
            <span className="panel-icon">📝</span>
            <h2>अभ्यर्थी विवरण</h2>
            <button
              className="nav-btn"
              onClick={() =>
                navigate("/PreviewDetails", {
                  state: { pdfName: candidates[currentIndex]?.folder },
                })
              }
            >
              Go to Preview
            </button>
          </div>

          {/* Section 1 — Basic Info */}
          <div className="form-section">
            <div className="section-title">
              <span className="section-num">1</span>मूल जानकारी
            </div>

            <div className="field-group">
              <label>पद का नाम</label>
              <select
                className={fieldClass("post")}
                value={formRef.current.post}
                onFocus={() => setSelectedField("post")}
                onChange={(e) => {
                  const u = { ...formRef.current, post: e.target.value };
                  setForm(u);
                  formRef.current = u;
                }}
              >
                <option value="">पद का नाम चुनें</option>

                {postOptions.map((p, i) => (
                  <option key={i} value={p.hindi}>
                    {p.hindi} ({p.english})
                  </option>
                ))}
              </select>

              {/* vibhag */}

              <div className="field-group">
                <label>विभाग</label>
                <select
                  className={fieldClass("vibhag")}
                  value={formRef.current.vibhag}
                  onFocus={() => setSelectedField("vibhag")}
                  onChange={(e) => {
                    const u = { ...formRef.current, vibhag: e.target.value };
                    setForm(u);
                    formRef.current = u;
                  }}
                >
                  <option value="">विभाग चुनें</option>

                  {vibhagOptions.map((v, i) => (
                    <option key={i} value={v.hindi}>
                      {v.hindi} ({v.english})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="two-col">
              <div className="field-group">
                <label>नाम (English)</label>
                <input
                  className={fieldClass("nameEnglish")}
                  placeholder="Full Name in English"
                  value={formRef.current.nameEnglish}
                  onFocus={() => setSelectedField("nameEnglish")}
                  onChange={(e) => {
                    const u = {
                      ...formRef.current,
                      nameEnglish: e.target.value,
                    };
                    setForm(u);
                    formRef.current = u;
                  }}
                />
              </div>
              <div className="field-group">
                <label>नाम (हिंदी)</label>
                <input
                  className={fieldClass("nameHindi")}
                  placeholder="पूरा नाम हिंदी में"
                  value={formRef.current.nameHindi}
                  onFocus={() => setSelectedField("nameHindi")}
                  onChange={(e) => {
                    const u = { ...formRef.current, nameHindi: e.target.value };
                    setForm(u);
                    formRef.current = u;
                  }}
                />
              </div>
            </div>

            <div className="two-col">
              <div className="field-group">
                <label>पिता / पति का नाम</label>
                <input
                  className={fieldClass("fatherName")}
                  placeholder="पिता / पति का नाम"
                  value={formRef.current.fatherName}
                  onFocus={() => setSelectedField("fatherName")}
                  onChange={(e) => {
                    const u = {
                      ...formRef.current,
                      fatherName: e.target.value,
                    };
                    setForm(u);
                    formRef.current = u;
                  }}
                />
              </div>
              <div className="field-group">
                <label>जन्म तिथि</label>
                <input
                  type="date"
                  className={fieldClass("dob")}
                  value={formRef.current.dob}
                  onFocus={() => setSelectedField("dob")}
                  onChange={(e) => {
                    const u = { ...formRef.current, dob: e.target.value };
                    setForm(u);
                    formRef.current = u;
                  }}
                />
              </div>
            </div>

            <div className="field-group">
              <label>लिंग</label>
              <div className="radio-group">
                {[
                  ["male", "पुरुष"],
                  ["female", "महिला"],
                  ["other", "अन्य"],
                ].map(([val, lbl]) => (
                  <label
                    key={val}
                    className={`radio-option ${formRef.current.gender === val ? "radio-active" : ""}`}
                  >
                    <input
                      type="radio"
                      name="gender"
                      value={val}
                      checked={formRef.current.gender === val}
                      onChange={(e) => {
                        const u = {
                          ...formRef.current,
                          gender: e.target.value,
                        };
                        setForm(u);
                        formRef.current = u;
                      }}
                    />
                    {lbl}
                  </label>
                ))}
              </div>
            </div>

            <div className="two-col">
              <div className="field-group">
                <label>श्रेणी</label>
                <select
                  className={fieldClass("category")}
                  value={formRef.current.category}
                  onFocus={() => setSelectedField("category")}
                  onChange={(e) => {
                    const u = { ...formRef.current, category: e.target.value };
                    setForm(u);
                    formRef.current = u;
                  }}
                >
                  <option value="">श्रेणी चुनें</option>
                  <option>General</option>
                  <option>EWS</option>
                  <option>OBC</option>
                  <option>SC</option>
                  <option>ST</option>
                </select>
              </div>
              <div className="field-group">
                <label>धर्म</label>
                <input
                  list="religion-options"
                  className={fieldClass("religion")}
                  placeholder="धर्म चुनें या लिखें"
                  value={formRef.current.religion}
                  onFocus={() => setSelectedField("religion")}
                  onChange={(e) => {
                    const u = { ...formRef.current, religion: e.target.value };
                    setForm(u);
                    formRef.current = u;
                  }}
                />
                <datalist id="religion-options">
                  <option value="Hindu" />
                  <option value="Muslim" />
                  <option value="Sikh" />
                  <option value="Christian" />
                  <option value="Jain" />
                  <option value="Buddhist" />
                  <option value="Other" />
                </datalist>
              </div>
            </div>

            <div className="two-col">
              <div className="field-group">
                <label>जाति</label>
                <input
                  className={fieldClass("caste")}
                  placeholder="जाति"
                  value={formRef.current.caste}
                  onFocus={() => setSelectedField("caste")}
                  onChange={(e) => {
                    const u = { ...formRef.current, caste: e.target.value };
                    setForm(u);
                    formRef.current = u;
                  }}
                />
              </div>
              <div className="field-group">
                <label>मोबाइल नंबर</label>
                <div>
                  <input
                    className={`${fieldClass("mobile")} ${validationErrors.mobile ? "error-field" : ""}`}
                    placeholder="मोबाइल नंबर (10 अंक)"
                    value={formRef.current.mobile}
                    onFocus={() => setSelectedField("mobile")}
                    onChange={(e) => handleMobileChange(e.target.value)}
                    maxLength={10}
                  />
                  {validationErrors.mobile && (
                    <small
                      style={{
                        color: "#ef4444",
                        marginTop: 4,
                        display: "block",
                      }}
                    >
                      ⚠️ {validationErrors.mobile}
                    </small>
                  )}
                </div>
              </div>
            </div>

            <div className="field-group">
              <label>ई-मेल आईडी</label>
              <div>
                <input
                  className={`${fieldClass("email")} ${validationErrors.email ? "error-field" : ""}`}
                  placeholder="ई-मेल आईडी (जैसे: name@example.com)"
                  value={formRef.current.email}
                  onFocus={() => setSelectedField("email")}
                  onChange={(e) => handleEmailChange(e.target.value)}
                />
                {validationErrors.email && (
                  <small
                    style={{ color: "#ef4444", marginTop: 4, display: "block" }}
                  >
                    ⚠️ {validationErrors.email}
                  </small>
                )}
              </div>
            </div>

            <div className="field-group">
              <label>आधार संख्या</label>
              <div>
                <input
                  className={`${fieldClass("aadharNumber")} ${validationErrors.aadhar ? "error-field" : ""}`}
                  placeholder="आधार संख्या (12 अंक)"
                  value={formRef.current.aadharNumber}
                  onFocus={() => setSelectedField("aadharNumber")}
                  onChange={(e) => handleAadharChange(e.target.value)}
                  maxLength={12}
                />
                {validationErrors.aadhar && (
                  <small
                    style={{ color: "#ef4444", marginTop: 4, display: "block" }}
                  >
                    ⚠️ {validationErrors.aadhar}
                  </small>
                )}
              </div>
            </div>

            <div className="field-group">
              <label>वर्तमान पता</label>
              <textarea
                className={`${fieldClass("address")} field-textarea`}
                placeholder="वर्तमान पता"
                value={formRef.current.address}
                onFocus={() => setSelectedField("address")}
                onChange={(e) => {
                  const newAddress = e.target.value;
                  const u = {
                    ...formRef.current,
                    address: newAddress,
                    correspondenceAddress: formRef.current.sameAddress
                      ? newAddress
                      : formRef.current.correspondenceAddress,
                  };
                  setForm(u);
                  formRef.current = u;
                }}
              />
            </div>

            {/* Checkbox */}
            <div className="field-group">
              <label
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <input
                  type="checkbox"
                  checked={formRef.current.sameAddress || false}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    const u = {
                      ...formRef.current,
                      sameAddress: isChecked,
                      correspondenceAddress: isChecked
                        ? formRef.current.address
                        : formRef.current.correspondenceAddress,
                    };
                    setForm(u);
                    formRef.current = u;
                  }}
                />
                वर्तमान पता ही पत्राचार पता है
              </label>
            </div>

            {/* Correspondence Address */}
            <div className="field-group">
              <label>पत्राचार पता</label>
              <textarea
                className={`${fieldClass("correspondenceAddress")} field-textarea`}
                placeholder="पत्राचार पता"
                value={formRef.current.correspondenceAddress}
                onFocus={() => setSelectedField("correspondenceAddress")}
                onChange={(e) => {
                  const newValue = e.target.value;
                  const u = {
                    ...formRef.current,
                    correspondenceAddress: newValue,
                    sameAddress: false,
                  };
                  setForm(u);
                  formRef.current = u;
                }}
                disabled={formRef.current.sameAddress}
              />
            </div>

            <div className="field-group">
              <label>पिन कोड</label>
              <div>
                <input
                  className={`${fieldClass("pinCode")} ${validationErrors.pinCode ? "error-field" : ""}`}
                  placeholder="पिन कोड (6 अंक)"
                  value={formRef.current.pinCode || ""}
                  onFocus={() => setSelectedField("pinCode")}
                  onChange={(e) => handlePinCodeChange(e.target.value)}
                  maxLength={6}
                />
                {validationErrors.pinCode && (
                  <small
                    style={{ color: "#ef4444", marginTop: 4, display: "block" }}
                  >
                    ⚠️ {validationErrors.pinCode}
                  </small>
                )}
              </div>
            </div>
          </div>

          {/* Section 2 — Educational Qualification */}
          <div className="form-section">
            <div className="section-title">
              <span className="section-num">2</span>शैक्षिक योग्यता
            </div>

            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>क्र.सं.</th>
                    <th>परीक्षा</th>
                    <th>बोर्ड</th>
                    <th>Score Type</th>
                    <th>पूर्णांक</th>
                    <th>प्राप्तांक</th>
                    <th>Result</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {eduRows.map((row, idx) => (
                    <tr key={row.id}>
                      <td>{idx + 1}</td>
                      <td>
                        <select
                          className="table-input"
                          value={row.exam || ""}
                          onChange={(e) =>
                            updateEduRow(row.id, "exam", e.target.value)
                          }
                        >
                          <option value="">Select</option>
                          <option value="Highschool">Highschool</option>
                          <option value="Intermediate">Intermediate</option>
                          <option value="Graduation">Graduation</option>
                          <option value="Post Graduation">
                            Post Graduation
                          </option>
                          <option value="CCC">CCC</option>
                          <option value="O-Level">O-Level</option>
                          <option value="Other">Other</option>
                        </select>
                        {row.exam === "Other" && (
                          <input
                            className="table-input"
                            placeholder="Custom Exam"
                            value={row.customExam}
                            onChange={(e) =>
                              updateEduRow(row.id, "customExam", e.target.value)
                            }
                          />
                        )}
                      </td>
                      <td>
                        <input
                          className="table-input"
                          value={row.board}
                          placeholder="Board"
                          onChange={(e) =>
                            updateEduRow(row.id, "board", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <select
                          className="table-input"
                          value={row.scoreType}
                          onChange={(e) =>
                            updateEduRow(row.id, "scoreType", e.target.value)
                          }
                        >
                          <option value="Percentage">Percentage</option>
                          <option value="CGPA">CGPA</option>
                          <option value="Grade">Grade</option>
                        </select>
                      </td>
                      <td>
                        <input
                          className="table-input small"
                          value={row.totalMarks}
                          placeholder="Total"
                          onChange={(e) =>
                            updateEduRow(row.id, "totalMarks", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="table-input small"
                          value={row.obtainedMarks}
                          placeholder={
                            row.scoreType === "CGPA"
                              ? "CGPA"
                              : row.scoreType === "Grade"
                                ? "Grade"
                                : "Marks"
                          }
                          onChange={(e) =>
                            updateEduRow(
                              row.id,
                              "obtainedMarks",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="table-input small"
                          value={row.percentage}
                          readOnly
                          placeholder={
                            row.scoreType === "CGPA"
                              ? "CGPA"
                              : row.scoreType === "Grade"
                                ? "Grade"
                                : "%"
                          }
                        />
                      </td>
                      <td>
                        <button onClick={() => deleteEduRow(row.id)}>🗑</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button className="add-row-btn" onClick={addEduRow}>
              + Add Qualification
            </button>
          </div>

          {/* Section 3 — Typing & Shorthand Skills */}
          <div className="form-section">
            <div className="section-title">
              <span className="section-num">3</span>टंकण एवं आशुलिपि की दक्षता
            </div>
            <div className="two-col">
              <div className="field-group">
                <label>हिंदी टंकण गति</label>
                <input
                  className={fieldClass("hindiTyping")}
                  placeholder="जैसे: 25 w/m"
                  value={formRef.current.hindiTyping}
                  onFocus={() => setSelectedField("hindiTyping")}
                  onChange={(e) => {
                    const u = {
                      ...formRef.current,
                      hindiTyping: e.target.value,
                    };
                    setForm(u);
                    formRef.current = u;
                  }}
                />
              </div>
              <div className="field-group">
                <label>अंग्रेजी टंकण गति</label>
                <input
                  className={fieldClass("englishTyping")}
                  placeholder="जैसे: 40 w/m"
                  value={formRef.current.englishTyping}
                  onFocus={() => setSelectedField("englishTyping")}
                  onChange={(e) => {
                    const u = {
                      ...formRef.current,
                      englishTyping: e.target.value,
                    };
                    setForm(u);
                    formRef.current = u;
                  }}
                />
              </div>
            </div>
            <div className="field-group">
              <label>आशुलिपि गति</label>
              <input
                className={fieldClass("shorthandSpeed")}
                placeholder="जैसे: 80 w/m"
                value={formRef.current.shorthandSpeed}
                onFocus={() => setSelectedField("shorthandSpeed")}
                onChange={(e) => {
                  const u = {
                    ...formRef.current,
                    shorthandSpeed: e.target.value,
                  };
                  setForm(u);
                  formRef.current = u;
                }}
              />
            </div>
          </div>

          {/* Section 4 — Work Experience */}
          <div className="form-section">
            <div className="section-title">
              <span className="section-num">4</span>कार्य अनुभव का विवरण
            </div>
            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>क्र.सं.</th>
                    <th>संस्था का नाम एवं पता</th>
                    <th>अवधि (कब से कब तक)</th>
                    <th>पद</th>
                    <th>वेतनमान</th>
                  </tr>
                </thead>
                <tbody>
                  {workRows.map((row) => (
                    <tr key={row.id}>
                      <td className="row-num">
                        {String(row.id).padStart(2, "0")}
                      </td>
                      <td>
                        <textarea
                          className="table-input table-textarea"
                          value={row.orgName}
                          placeholder="संस्था का नाम एवं पता"
                          onChange={(e) =>
                            updateWorkRow(row.id, "orgName", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="table-input"
                          value={row.duration}
                          placeholder="01/2020 से 03/2023"
                          onChange={(e) =>
                            updateWorkRow(row.id, "duration", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="table-input"
                          value={row.post}
                          placeholder="पद"
                          onChange={(e) =>
                            updateWorkRow(row.id, "post", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="table-input"
                          value={row.salary}
                          placeholder="वेतन"
                          onChange={(e) =>
                            updateWorkRow(row.id, "salary", e.target.value)
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button className="add-row-btn" onClick={addWorkRow}>
              + पंक्ति जोड़ें
            </button>
          </div>

          {/* Section 5 — Payment Information */}
          <div className="form-section">
            <div className="section-title">
              <span className="section-num">5</span>भुगतान जानकारी
            </div>
            <div className="two-col">
              <div className="field-group">
                <label>भुगतान विधि</label>
                <select
                  className={fieldClass("paymentMethod")}
                  value={formRef.current.paymentMethod}
                  onFocus={() => setSelectedField("paymentMethod")}
                  onChange={(e) => {
                    const u = {
                      ...formRef.current,
                      paymentMethod: e.target.value,
                      paymentNumber: "",
                    };
                    setForm(u);
                    formRef.current = u;
                  }}
                >
                  <option value="">भुगतान विधि चुनें</option>
                  {paymentOptions.map((m, i) => (
                    <option key={i} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field-group">
                <label>
                  {formRef.current.paymentMethod === "Check"
                    ? "चेक नंबर"
                    : formRef.current.paymentMethod === "UPI"
                      ? "UPI आईडी"
                      : formRef.current.paymentMethod === "Bank"
                        ? "बैंक खाता संख्या"
                        : formRef.current.paymentMethod === "NEFT"
                          ? "NEFT लेन-देन संख्या"
                          : formRef.current.paymentMethod === "IMPS"
                            ? "IMPS लेन-देन संख्या"
                            : formRef.current.paymentMethod === "RTGS"
                              ? "RTGS लेन-देन संख्या"
                              : "भुगतान संख्या"}
                </label>
                <input
                  className={fieldClass("paymentNumber")}
                  type="text"
                  value={formRef.current.paymentNumber}
                  disabled={!formRef.current.paymentMethod}
                  onFocus={() => setSelectedField("paymentNumber")}
                  onChange={(e) => {
                    const u = {
                      ...formRef.current,
                      paymentNumber: e.target.value,
                    };
                    setForm(u);
                    formRef.current = u;
                  }}
                />
                {!formRef.current.paymentMethod && (
                  <small
                    style={{ color: "#666", marginTop: 4, display: "block" }}
                  >
                    पहले भुगतान विधि चुनें
                  </small>
                )}
              </div>
            </div>
          </div>

          {/* Section 6 — Photo & Signature */}
          <div className="form-section">
            <div className="section-title">
              <span className="section-num">6</span>फोटो एवं हस्ताक्षर (PDF से)
            </div>

            <div
              style={{
                fontSize: 13,
                color: "#4a5568",
                marginBottom: 14,
                background: "linear-gradient(135deg, #ebf4ff, #e8f0fe)",
                padding: "10px 14px",
                borderRadius: 8,
                border: "1px solid #c3d9f7",
                display: "flex",
                alignItems: "flex-start",
                gap: 8,
              }}
            >
              <span style={{ fontSize: 16, flexShrink: 0 }}>📌</span>
              <div>
                <strong>निर्देश:</strong> PDF पर माउस से क्षेत्र खींचें (drag
                करें), फिर नीचे
                <strong> "Save Photo"</strong> या{" "}
                <strong>"Save Signature"</strong> बटन दबाएं।
              </div>
            </div>

            {cropPreview && (
              <div
                style={{
                  marginBottom: 16,
                  padding: "12px 14px",
                  background: "#f0fdf4",
                  border: "1px solid #86efac",
                  borderRadius: 10,
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                }}
              >
                <img
                  src={cropPreview.base64}
                  alt="crop preview"
                  style={{
                    height: 80,
                    maxWidth: 200,
                    objectFit: "contain",
                    border: "2px solid #4ade80",
                    borderRadius: 6,
                    background: "#fff",
                  }}
                />
                <div>
                  <div
                    style={{
                      fontWeight: 700,
                      color: "#15803d",
                      fontSize: 14,
                      marginBottom: 4,
                    }}
                  >
                    {cropPreview.type === "photo"
                      ? "📸 फोटो Preview"
                      : "✍️ हस्ताक्षर Preview"}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "#166534",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <span
                      style={{
                        display: "inline-block",
                        width: 12,
                        height: 12,
                        border: "2px solid #16a34a",
                        borderTopColor: "transparent",
                        borderRadius: "50%",
                        animation: "ocr-spin 0.7s linear infinite",
                      }}
                    />
                    Upload हो रहा है…
                  </div>
                </div>
              </div>
            )}

            <div className="two-col">
              <button
                onClick={() => uploadCroppedImage("photo")}
                disabled={!!photoSignUploading}
                style={{
                  padding: "11px 16px",
                  borderRadius: 8,
                  border: "none",
                  cursor: photoSignUploading ? "not-allowed" : "pointer",
                  fontWeight: 700,
                  fontSize: 14,
                  color: "#fff",
                  background:
                    photoSignUploading === "photo"
                      ? "#9ca3af"
                      : "linear-gradient(135deg, #1d4ed8, #1e40af)",
                  boxShadow:
                    photoSignUploading === "photo"
                      ? "none"
                      : "0 2px 8px rgba(29,78,216,0.35)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  transition: "all 0.2s ease",
                  width: "100%",
                }}
              >
                {photoSignUploading === "photo" ? (
                  <>
                    <span
                      style={{
                        display: "inline-block",
                        width: 14,
                        height: 14,
                        border: "2px solid rgba(255,255,255,0.5)",
                        borderTopColor: "#fff",
                        borderRadius: "50%",
                        animation: "ocr-spin 0.7s linear infinite",
                        flexShrink: 0,
                      }}
                    />
                    सहेज रहा है…
                  </>
                ) : (
                  <>📸 Save Photo (PDF से)</>
                )}
              </button>

              <button
                onClick={() => uploadCroppedImage("signature")}
                disabled={!!photoSignUploading}
                style={{
                  padding: "11px 16px",
                  borderRadius: 8,
                  border: "none",
                  cursor: photoSignUploading ? "not-allowed" : "pointer",
                  fontWeight: 700,
                  fontSize: 14,
                  color: "#fff",
                  background:
                    photoSignUploading === "signature"
                      ? "#9ca3af"
                      : "linear-gradient(135deg, #7c3aed, #6d28d9)",
                  boxShadow:
                    photoSignUploading === "signature"
                      ? "none"
                      : "0 2px 8px rgba(124,58,237,0.35)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  transition: "all 0.2s ease",
                  width: "100%",
                }}
              >
                {photoSignUploading === "signature" ? (
                  <>
                    <span
                      style={{
                        display: "inline-block",
                        width: 14,
                        height: 14,
                        border: "2px solid rgba(255,255,255,0.5)",
                        borderTopColor: "#fff",
                        borderRadius: "50%",
                        animation: "ocr-spin 0.7s linear infinite",
                        flexShrink: 0,
                      }}
                    />
                    सहेज रहा है…
                  </>
                ) : (
                  <>✍️ Save Signature (PDF से)</>
                )}
              </button>
            </div>

            <p
              style={{
                fontSize: 11,
                color: "#9ca3af",
                marginTop: 10,
                textAlign: "center",
              }}
            >
              ✅ Save होने के बाद crop अपने आप reset हो जाएगा
            </p>
          </div>

          {/* Update + Delete at bottom */}
          <div className="action-bar-bottom">
            <button
              className="action-btn update wide"
              onClick={updateCandidate}
            >
              ✏️ अपडेट करें
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProcessedResumeOCR;